import type { Query } from '@tanstack/react-query';
import type { CampaignCandidateResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import type {
  CampaignFacets,
  CampaignJobboard,
  CampaignStats,
  CampaignTab,
} from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';

import { queryClient } from 'config/queryClient';
import { useRJCommunicationHubStore } from '@/stores/rj-communication-hub/rj-communication-hub.store';
import { isObject } from 'helpers/helpers';
import { Candidate } from 'model/Candidate/Candidate';
import { QueryKey } from 'types/query';
import { resetQueryWhenNoObservers } from 'utils/resetQueryWhenNoObservers';
import { resetSimilarQueriesButCurrent } from 'utils/resetSimilarQueriesButCurrent';

type PaginatedCandidatesData = {
  data: Candidate[];
  total: number;
};

type PaginatedCandidatesResults = {
  results: Candidate[];
  count: number;
};

type PaginatedCandidates = PaginatedCandidatesData | PaginatedCandidatesResults;

const isPaginatedCandidatesData = (data: any): data is PaginatedCandidatesData => {
  return data && Array.isArray(data.data) && typeof data.total === 'number';
};

const isPaginatedCandidatesResults = (data: any): data is PaginatedCandidatesResults => {
  return data && Array.isArray(data.results) && typeof data.count === 'number';
};

const isPaginatedCandidates = (data: any): data is PaginatedCandidates => {
  return isPaginatedCandidatesData(data) || isPaginatedCandidatesResults(data);
};

const isCandidate = (data: any): data is Candidate => {
  return data instanceof Candidate;
};

const getCandidatesArray = (cache: PaginatedCandidates): Candidate[] => {
  if (isPaginatedCandidatesData(cache)) {
    return cache.data;
  }
  return cache.results;
};

const createUpdatedCache = (cache: PaginatedCandidates, newCandidates: Candidate[]): PaginatedCandidates => {
  if (isPaginatedCandidatesData(cache)) {
    return { ...cache, data: newCandidates };
  }
  return { ...cache, results: newCandidates };
};

const removeCandidate = (cache: any, esPersonId: string) => {
  if (isObject(cache) && isPaginatedCandidates(cache)) {
    const candidates = getCandidatesArray(cache);
    const filteredCandidates = candidates.filter((candidate) => candidate.es_person_id !== esPersonId);

    return createUpdatedCache(cache, filteredCandidates);
  }

  return cache;
};

export const getActiveQueryKey = <T>(baseQueryKey: QueryKey): T | undefined => {
  const queryCache = queryClient.getQueryCache();
  const allQueries = queryCache.getAll();

  const activeQueries: Query[] = [];

  allQueries.forEach((query) => {
    if (
      query.getObserversCount() > 0 &&
      query.queryKey[0] === baseQueryKey &&
      queryClient.getQueryData(query.queryKey)
    ) {
      activeQueries.push(query);
    }
  });

  return activeQueries[0]?.queryKey as T | undefined;
};

export const decreaseStatCount = (tab: CampaignTab) => {
  const decreaseStat = (queryKey: [QueryKey], tab: CampaignTab) => {
    queryClient.setQueryData(queryKey, (stats: CampaignStats | undefined) => {
      if (!stats) throw Error('Set stats undefined data');

      return { ...stats, [tab]: stats[tab] - 1 };
    });
  };
  const statQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.selectedCampaignContextUseStats);
  const ratedStatQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.useRatedCandidatesStats);

  if (ratedStatQueryKey) decreaseStat(ratedStatQueryKey, tab);
  if (statQueryKey) decreaseStat(statQueryKey, tab);
};

export const increaseStatCount = (tab: CampaignTab) => {
  const increaseStat = (queryKey: [QueryKey], tab: CampaignTab) => {
    queryClient.setQueryData(queryKey, (stats: CampaignStats | undefined) => {
      if (!stats) throw Error('Set stats undefined data');

      return { ...stats, [tab]: stats[tab] + 1 };
    });
  };

  const statQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.selectedCampaignContextUseStats);
  const ratedStatQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.useRatedCandidatesStats);

  if (ratedStatQueryKey) increaseStat(ratedStatQueryKey, tab);
  if (statQueryKey) increaseStat(statQueryKey, tab);
};

export const decreaseJobboardCount = (jobBoard: CampaignJobboard) => {
  const jobboardQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.selectedCampaignContextUseFacets);

  if (jobboardQueryKey) {
    queryClient.setQueryData(jobboardQueryKey, (facets: CampaignFacets | undefined) => {
      if (!facets) throw Error('Set stats undefined data');

      return { ...facets, [jobBoard]: facets[jobBoard] - 1 };
    });
  }
};

export const resetList = (tab: CampaignTab) => {
  queryClient.resetQueries([QueryKey.selectedCampaignContextUseCandidates, { tab }], { exact: false });
};

export const resetCampaignCandidatesQueryIfCurrentPageEmpty = (campaignId: number) => {
  const queryCache = queryClient.getQueryCache();

  for (const query of queryCache.getAll()) {
    const key = query.queryKey;
    if (key[0] !== QueryKey.selectedCampaignContextUseCandidates) continue;

    const params = key[1] as { id?: number } | undefined;
    if (params?.id !== campaignId) continue;
    if (query.getObserversCount() === 0) continue;

    const data = queryClient.getQueryData(key);
    if (!isObject(data) || !isPaginatedCandidates(data)) continue;
    if (getCandidatesArray(data).length > 0) continue;

    void queryClient.resetQueries(key);
    return;
  }
};

export const updateCandidateQueries = (
  queryKeys: QueryKey[],
  esPersonId: string,
  update: Partial<CampaignCandidateResponse>,
  { remove = false } = {},
) => {
  const queryCache = queryClient.getQueryCache();
  const allQueries = queryCache.getAll();
  allQueries.forEach((query) => {
    const queryKey = query.queryKey[0] as QueryKey;

    if (query.getObserversCount() > 0 && queryKeys.includes(queryKey) && queryClient.getQueryData(query.queryKey)) {
      queryClient.setQueryData(query.queryKey, (oldData: any) =>
        updateCandidateAtCampaignAndRatedCandidates(oldData, esPersonId, update, { remove }),
      );

      resetSimilarQueriesButCurrent(query.queryKey);
      resetQueryWhenNoObservers(query.queryKey);
    }
  });

  if (remove) {
    useRJCommunicationHubStore.getState().removeBulkCandidateId(esPersonId);
  }
};

const updateCandidateAtCampaignAndRatedCandidates = (
  cache: any,
  esPersonId: string,
  update: Partial<CampaignCandidateResponse>,
  { remove = false } = {},
) => {
  if (remove) {
    return removeCandidate(cache, esPersonId);
  }

  if (isObject(cache) && isPaginatedCandidates(cache)) {
    const candidates = getCandidatesArray(cache);
    const updatedCandidates = candidates.map((candidate) => {
      if (candidate.es_person_id === esPersonId) {
        const newCandidate = Candidate.clone(candidate);
        newCandidate.mergeLocally(update);
        return newCandidate;
      }
      return candidate;
    });

    return createUpdatedCache(cache, updatedCandidates);
  }

  if (isCandidate(cache) && cache.es_person_id === esPersonId) {
    const newCandidate = Candidate.clone(cache);
    newCandidate.mergeLocally(update);
    return newCandidate;
  }

  return cache;
};

export const updateCandidateAtSharedAndCandidate = (esPersonId: string, update: Partial<CampaignCandidateResponse>) => {
  const activeQueryKey = getActiveQueryKey<[QueryKey]>(QueryKey.sharedCandidateContextUseCandidate);

  if (activeQueryKey) {
    queryClient.setQueryData(activeQueryKey, (cache: any) => {
      if (isObject(cache) && isCandidate(cache) && cache.es_person_id === esPersonId) {
        const newCandidate = Candidate.clone(cache);
        newCandidate.mergeLocally(update);
        return newCandidate;
      }

      return cache;
    });
  }
};
